import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
const LOOP = 2.2;
const UTENSILS = [{
  x: 104,
  delay: 0
}, {
  x: 158,
  delay: 0.5
}, {
  x: 212,
  delay: 1
}];
const DICE = [{
  x: 66,
  delay: 0
}, {
  x: 84,
  delay: 0.25
}, {
  x: 102,
  delay: 0.5
}];
const SALT = [{
  x: 250,
  delay: 0
}, {
  x: 264,
  delay: 0.4
}, {
  x: 278,
  delay: 0.8
}, {
  x: 292,
  delay: 1.2
}];

/**
 * A hairline kitchen in motion: utensils swaying on the rail, a knife rocking
 * through a dice, salt falling, and a pan tossing a crêpe on a loop.
 * Continuous illustrative motion — held completely still for reduced motion.
 */
export function PanFlipScene({
  className
}: {
  className?: string;
}) {
  const reduce = useReducedMotion();
  const ink = 'rgb(var(--c-ink))';
  const soft = 'rgb(var(--c-ink-2))';
  const hair = 'rgb(var(--c-hairline))';
  return <svg viewBox="0 0 520 340" className={className} fill="none" aria-hidden="true" role="presentation" data-id="element-11" data-mp="1zh6y24s6dn8g">
      {/* hanging rail */}
      <line x1="52" y1="26" x2="286" y2="26" stroke={ink} strokeWidth="2.4" strokeLinecap="round" data-id="element-12" data-mp="3lyzvk1wxc58s" />
      <line x1="60" y1="16" x2="60" y2="26" stroke={hair} strokeWidth="2" strokeLinecap="round" data-id="element-13" data-mp="3qw9klggeiew3" />
      <line x1="278" y1="16" x2="278" y2="26" stroke={hair} strokeWidth="2" strokeLinecap="round" data-id="element-14" data-mp="3cw8mu9vfnnku" />

      {UTENSILS.map((u, i) => <motion.g key={u.x} style={{
      transformOrigin: `${u.x}px 26px`,
      transformBox: 'view-box'
    }} initial={{
      rotate: 0
    }} animate={reduce ? {
      rotate: 0
    } : {
      rotate: [-2.4, 2.4, -2.4]
    }} transition={reduce ? undefined : {
      duration: 4.2,
      repeat: Infinity,
      delay: u.delay,
      ease: 'easeInOut'
    }} stroke={soft} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" data-id="element-15" data-mp="3hxxzftyb09b1" data-mpi={i}>
          <circle cx={u.x} cy="26" r="3.4" stroke={ink} strokeWidth="2" data-id="element-16" data-mp="2kap4z9gbrpww" />
          <line x1={u.x} y1="30" x2={u.x} y2={i === 1 ? 74 : 68} data-id="element-17" data-mp="2p6wfzgnkvrlp" />
          {i === 0 ? <>
              <path d={`M${u.x} 68c-11 6-13 20-3 26`} data-id="element-18" data-mp="usiizkxvceh8" />
              <path d={`M${u.x} 68c11 6 13 20 3 26`} data-id="element-19" data-mp="q08vebk87qi4" />
              <path d={`M${u.x} 68v26`} data-id="element-20" data-mp="pyedl6494ei9" />
            </> : null}
          {i === 1 ? <rect x={u.x - 9} y="74" width="18" height="24" rx="4" data-id="element-21" data-mp="lwsyde48vo4w" /> : null}
          {i === 2 ? <path d={`M${u.x - 11} 68a11 11 0 0 0 22 0`} data-id="element-22" data-mp="gxpye834g83s" /> : null}
        </motion.g>)}

      {/* counter */}
      <line x1="10" y1="300" x2="510" y2="300" stroke={ink} strokeWidth="2" strokeLinecap="round" data-id="element-23" data-mp="19ohznjz1nmxz" />
      <line x1="24" y1="312" x2="300" y2="312" stroke={hair} strokeWidth="1.5" strokeDasharray="2 9" data-id="element-24" data-mp="14pervrroj2mb" />

      {/* board, dice and rocking knife */}
      <rect x="34" y="276" width="164" height="20" rx="6" stroke={soft} strokeWidth="2.2" fill="rgb(var(--c-oat))" data-id="element-25" data-mp="zpinhrf2l968" />
      <line x1="46" y1="282" x2="46" y2="290" stroke={hair} strokeWidth="2" strokeLinecap="round" data-id="element-26" data-mp="3pgxg3rudrmqp" />
      {DICE.map(d => <motion.rect key={d.x} x={d.x} y="266" width="10" height="10" rx="2" stroke="rgb(var(--c-action))" strokeWidth="2" fill="rgb(var(--c-action-soft))" initial={{
      opacity: 0.35
    }} animate={reduce ? {
      opacity: 0.9
    } : {
      opacity: [0.25, 1, 0.25]
    }} transition={reduce ? undefined : {
      duration: 2.4,
      repeat: Infinity,
      delay: d.delay,
      ease: 'easeInOut'
    }} data-id="element-27" data-mp="3kivtwy1nr7b0" />)}
      <motion.g style={{
      transformOrigin: '116px 272px',
      transformBox: 'view-box'
    }} initial={{
      rotate: 0
    }} animate={reduce ? {
      rotate: -6
    } : {
      rotate: [0, -15, 0]
    }} transition={reduce ? undefined : {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-28" data-mp="1wj4sy1b87hq">
        {/* blade: straight spine on top, curved cutting edge along the board */}
        <path d="M116 262 L176 262 L176 272 Q148 276 116 262 Z" stroke={ink} strokeWidth="2.2" strokeLinejoin="round" fill="rgb(var(--c-surface))" data-id="element-29" data-mp="2dmbxbg33j6ij" />
        <line x1="176" y1="264" x2="176" y2="271" stroke={ink} strokeWidth="2.2" strokeLinecap="round" data-id="element-30" data-mp="2yyjzcnmv48s" />
        {/* handle */}
        <line x1="178" y1="266" x2="208" y2="262" stroke={ink} strokeWidth="5.5" strokeLinecap="round" data-id="element-31" data-mp="gyzor370qvtj" />
      </motion.g>

      {/* salt falling into the pan */}
      {SALT.map(s => <motion.circle key={s.x} cx={s.x} cy="150" r="2" fill={soft} initial={{
      opacity: 0,
      y: 0
    }} animate={reduce ? {
      opacity: 0.5,
      y: 20
    } : {
      opacity: [0, 0.8, 0],
      y: [0, 34, 46]
    }} transition={reduce ? undefined : {
      duration: 1.8,
      repeat: Infinity,
      delay: s.delay,
      ease: 'easeIn'
    }} data-id="element-32" data-mp="3keydzddnxfp0" />)}

      {/* burner flames */}
      <g stroke="rgb(var(--c-accent))" strokeWidth="2.4" strokeLinecap="round" data-id="element-33" data-mp="31ndy9bymp1t0">
        {[{
        x: 240,
        d: 0
      }, {
        x: 268,
        d: 0.35
      }, {
        x: 296,
        d: 0.7
      }].map(tick => <motion.path key={tick.x} d={`M${tick.x} 284 C ${tick.x - 6} 274, ${tick.x + 6} 268, ${tick.x} 256`} initial={{
        opacity: 0.25
      }} animate={reduce ? {
        opacity: 0.5
      } : {
        opacity: [0.25, 0.8, 0.25]
      }} transition={reduce ? undefined : {
        duration: 1.6,
        repeat: Infinity,
        delay: tick.d,
        ease: 'easeInOut'
      }} data-id="element-34" data-mp="1c6jsdgqptvxw" />)}
      </g>

      {/* steam */}
      <g stroke="rgb(var(--c-ink-2))" strokeWidth="1.8" strokeLinecap="round" data-id="element-35" data-mp="1zxp71skx6i6m">
        {[{
        d: 'M232 150c-8-8 6-14-2-22',
        delay: 0
      }, {
        d: 'M268 142c-9-9 7-15-1-24',
        delay: 0.9
      }, {
        d: 'M304 152c-8-8 6-14-2-22',
        delay: 1.8
      }].map(wisp => <motion.path key={wisp.d} d={wisp.d} initial={{
        opacity: 0,
        y: 6
      }} animate={reduce ? {
        opacity: 0.4,
        y: 0
      } : {
        opacity: [0, 0.5, 0],
        y: [8, -14, -26]
      }} transition={reduce ? undefined : {
        duration: 2.8,
        repeat: Infinity,
        delay: wisp.delay,
        ease: 'easeOut'
      }} data-id="element-36" data-mp="air2py7q4j09" />)}
      </g>

      {/* pan — pivots at the grip so the bowl lifts on the toss */}
      <motion.g style={{
      transformOrigin: '458px 210px',
      transformBox: 'view-box'
    }} initial={{
      rotate: 0
    }} animate={reduce ? {
      rotate: 0
    } : {
      rotate: [0, 3, -15, 2, 0]
    }} transition={reduce ? undefined : {
      duration: LOOP,
      repeat: Infinity,
      repeatDelay: 0.35,
      times: [0, 0.12, 0.3, 0.68, 0.88],
      ease: 'easeInOut'
    }} data-id="element-37" data-mp="3iqpe8lna0ip9">
        <path d="M210 200 L215 235 Q268 256 321 235 L326 200" stroke={ink} strokeWidth="2.6" strokeLinejoin="round" fill="rgb(var(--c-oat))" data-id="element-38" data-mp="2uwfjof68vca1" />
        <line x1="204" y1="200" x2="332" y2="200" stroke={ink} strokeWidth="4.5" strokeLinecap="round" data-id="element-39" data-mp="t3zui11gm071" />
        <path d="M332 203 C 368 200, 410 206, 456 210" stroke={ink} strokeWidth="5" strokeLinecap="round" data-id="element-40" data-mp="10kmstknv9194" />
        <line x1="410" y1="207" x2="412" y2="219" stroke={hair} strokeWidth="2.4" strokeLinecap="round" data-id="element-41" data-mp="15jhagja3kjrn" />
        <line x1="432" y1="208" x2="434" y2="220" stroke={hair} strokeWidth="2.4" strokeLinecap="round" data-id="element-42" data-mp="1fbndqp5sunm5" />

        {/* crêpe — leaves the pan, flips, comes back */}
        <motion.g style={{
        transformOrigin: '268px 198px',
        transformBox: 'view-box'
      }} initial={{
        y: 0,
        x: 0,
        rotate: 0
      }} animate={reduce ? {
        y: 0,
        x: 0,
        rotate: 0
      } : {
        y: [0, 0, -96, 0, 0],
        x: [0, 0, -18, 0, 0],
        rotate: [0, 0, 190, 360, 360]
      }} transition={reduce ? undefined : {
        duration: LOOP,
        repeat: Infinity,
        repeatDelay: 0.35,
        times: [0, 0.14, 0.46, 0.8, 1],
        ease: ['linear', 'easeOut', 'easeIn', 'linear']
      }} data-id="element-43" data-mp="hpfy8k7yjav0">
          <ellipse cx="268" cy="198" rx="44" ry="11" fill="rgb(var(--c-action-soft))" stroke="rgb(var(--c-action))" strokeWidth="2.4" data-id="element-44" data-mp="2o6bhu59eo2tg" />
          <path d="M247 196c6 3 17 4 27 1" stroke="rgb(var(--c-action))" strokeWidth="1.8" strokeLinecap="round" data-id="element-45" data-mp="2y24wpvalk8ee" />
        </motion.g>
      </motion.g>
    </svg>;
}